import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";

// ---------------------------------------------------------------------------
// Family Banking Stewardship™ Assessment — 100-point weighted model
// ---------------------------------------------------------------------------

export const FUTURE_ME_QUESTIONS: string[] = [
  "Why am I taking this loan?",
  "What positive outcome do I expect this loan to create?",
  "Why is it important to me and to my family that I follow through on this repayment commitment?",
];

/** Items the system can detect automatically from the user's scenario. */
export interface StewardshipAuto {
  purposeSelected: boolean;        // A: 10
  scheduleCreated: boolean;        // A: 5
  paymentEstablished: boolean;     // B: 5
  timelineEstablished: boolean;    // B: 5
  marginIdentified: boolean;       // B: 10
}

/** Items the user checks manually. */
export interface StewardshipManual {
  commitmentSigned: boolean;       // A: 5
  accountabilityPartner: boolean;  // C: 10
  familyInformed: boolean;         // C: 5
  spouseDiscussion: boolean;       // C: 5
  recurringPayment: boolean;       // D: 10
  calendarReminders: boolean;      // D: 5
  anniversaryReminders: boolean;   // D: 5
  familyForumPlanned: boolean;     // E: 5
  marginRedirectStrategy: boolean; // E: 5
  stewardshipDiscussion: boolean;  // E: 5
  nextReviewDate: boolean;         // E: 5
}

export interface StewardshipState {
  manual: StewardshipManual;
  /** Snapshot of auto items at last update — persisted for downstream consumers (PDF, advisor view). */
  auto: StewardshipAuto;
  /** Computed score 0–100, persisted. */
  score: number;
  /** Computed rating, persisted. */
  rating: string;
}

export interface FutureMeState {
  answers: string[]; // length 3
}

export function emptyStewardshipManual(): StewardshipManual {
  return {
    commitmentSigned: false,
    accountabilityPartner: false,
    familyInformed: false,
    spouseDiscussion: false,
    recurringPayment: false,
    calendarReminders: false,
    anniversaryReminders: false,
    familyForumPlanned: false,
    marginRedirectStrategy: false,
    stewardshipDiscussion: false,
    nextReviewDate: false,
  };
}

export function emptyStewardshipAuto(): StewardshipAuto {
  return {
    purposeSelected: false,
    scheduleCreated: false,
    paymentEstablished: false,
    timelineEstablished: false,
    marginIdentified: false,
  };
}

export function emptyStewardship(): StewardshipState {
  return {
    manual: emptyStewardshipManual(),
    auto: emptyStewardshipAuto(),
    score: 0,
    rating: stewardshipRating(0),
  };
}

export function emptyFutureMe(): FutureMeState {
  return { answers: FUTURE_ME_QUESTIONS.map(() => "") };
}

/**
 * Migrate legacy `{ answers: boolean[] }` shape from older saved scenarios
 * into the new structured shape. Unknown fields default to false.
 */
export function normalizeStewardship(raw: unknown): StewardshipState {
  if (!raw || typeof raw !== "object") return emptyStewardship();
  const r = raw as Record<string, unknown>;
  // Legacy shape
  if (Array.isArray(r.answers)) {
    return emptyStewardship();
  }
  const manual: StewardshipManual = { ...emptyStewardshipManual(), ...((r.manual as Partial<StewardshipManual>) ?? {}) };
  const auto: StewardshipAuto = { ...emptyStewardshipAuto(), ...((r.auto as Partial<StewardshipAuto>) ?? {}) };
  const score = typeof r.score === "number" ? r.score : computeStewardshipScore(manual, auto);
  const rating = typeof r.rating === "string" ? r.rating : stewardshipRating(score);
  return { manual, auto, score, rating };
}

// Point weights ------------------------------------------------------------

export const AUTO_POINTS: Record<keyof StewardshipAuto, number> = {
  purposeSelected: 10,
  scheduleCreated: 5,
  paymentEstablished: 5,
  timelineEstablished: 5,
  marginIdentified: 10,
};

export const MANUAL_POINTS: Record<keyof StewardshipManual, number> = {
  commitmentSigned: 5,
  accountabilityPartner: 10,
  familyInformed: 5,
  spouseDiscussion: 5,
  recurringPayment: 10,
  calendarReminders: 5,
  anniversaryReminders: 5,
  familyForumPlanned: 5,
  marginRedirectStrategy: 5,
  stewardshipDiscussion: 5,
  nextReviewDate: 5,
};

export function computeStewardshipScore(manual: StewardshipManual, auto: StewardshipAuto): number {
  let total = 0;
  (Object.keys(AUTO_POINTS) as (keyof StewardshipAuto)[]).forEach((k) => {
    if (auto[k]) total += AUTO_POINTS[k];
  });
  (Object.keys(MANUAL_POINTS) as (keyof StewardshipManual)[]).forEach((k) => {
    if (manual[k]) total += MANUAL_POINTS[k];
  });
  return total;
}

export function stewardshipScore(s: StewardshipState): number {
  return typeof s.score === "number" ? s.score : computeStewardshipScore(s.manual, s.auto);
}

export function stewardshipRating(score: number): string {
  if (score >= 90) return "Legacy Builder";
  if (score >= 75) return "Strong Steward";
  if (score >= 60) return "Committed Banker";
  if (score >= 40) return "Needs Attention";
  return "Loan Drift Risk";
}

/** True when the user has engaged with the assessment (any manual check or any auto credit). */
export function stewardshipCompleted(s: StewardshipState): boolean {
  if (!s) return false;
  const anyManual = Object.values(s.manual).some(Boolean);
  const anyAuto = Object.values(s.auto).some(Boolean);
  return anyManual || anyAuto;
}

export function futureMeHasContent(f: FutureMeState): boolean {
  return f.answers.some((a) => a.trim().length > 0);
}

// Structured item list for rendering (UI + PDF) ----------------------------

type ItemKind = "manual" | "auto";
interface AssessmentItem {
  section: "A" | "B" | "C" | "D" | "E";
  key: keyof StewardshipManual | keyof StewardshipAuto;
  kind: ItemKind;
  label: string;
  points: number;
}

export const SECTION_TITLES: Record<AssessmentItem["section"], string> = {
  A: "A. Purpose & Commitment",
  B: "B. Repayment Structure",
  C: "C. Accountability",
  D: "D. Automation",
  E: "E. Stewardship Habits",
};

export const STEWARDSHIP_ITEMS: AssessmentItem[] = [
  // A. Purpose & Commitment — 20
  { section: "A", key: "purposeSelected",        kind: "auto",   label: "Purpose selected",                                              points: 10 },
  { section: "A", key: "scheduleCreated",        kind: "auto",   label: "Repayment schedule created",                                    points: 5  },
  { section: "A", key: "commitmentSigned",       kind: "manual", label: "Commitment Agreement signed or intended to be signed",          points: 5  },
  // B. Repayment Structure — 20
  { section: "B", key: "paymentEstablished",     kind: "auto",   label: "Payment amount established",                                    points: 5  },
  { section: "B", key: "timelineEstablished",    kind: "auto",   label: "Repayment timeline established",                                points: 5  },
  { section: "B", key: "marginIdentified",       kind: "auto",   label: "Family Financing Margin™ identified",                          points: 10 },
  // C. Accountability — 20
  { section: "C", key: "accountabilityPartner",  kind: "manual", label: "Accountability partner identified",                             points: 10 },
  { section: "C", key: "familyInformed",         kind: "manual", label: "Family members affected by this loan have been (or will be) informed", points: 5 },
  { section: "C", key: "spouseDiscussion",       kind: "manual", label: "Spouse / partner discussion completed or planned (if applicable)", points: 5 },
  // D. Automation — 20
  { section: "D", key: "recurringPayment",       kind: "manual", label: "Recurring payment scheduled in online banking or payment system", points: 10 },
  { section: "D", key: "calendarReminders",      kind: "manual", label: "Calendar reminders created for payment review",                 points: 5  },
  { section: "D", key: "anniversaryReminders",   kind: "manual", label: "Policy anniversary reminders created",                          points: 5  },
  // E. Stewardship Habits — 20
  { section: "E", key: "familyForumPlanned",     kind: "manual", label: "Family Financial Forum™ planned",                              points: 5  },
  { section: "E", key: "marginRedirectStrategy", kind: "manual", label: "Family Financing Margin™ redirect strategy established",       points: 5  },
  { section: "E", key: "stewardshipDiscussion",  kind: "manual", label: "Stewardship discussion planned with relevant family members",   points: 5  },
  { section: "E", key: "nextReviewDate",         kind: "manual", label: "Next review date selected",                                     points: 5  },
];

// ---------------------------------------------------------------------------
// React component
// ---------------------------------------------------------------------------

interface Props {
  stewardship: StewardshipState;
  onStewardshipChange: (s: StewardshipState) => void;
  futureMe: FutureMeState;
  onFutureMeChange: (f: FutureMeState) => void;
  /** Live auto-detected signals derived from the current scenario. */
  autoContext: StewardshipAuto;
}

export function StewardshipAndFutureMe({
  stewardship,
  onStewardshipChange,
  futureMe,
  onFutureMeChange,
  autoContext,
}: Props) {
  const [open, setOpen] = useState(false);

  function StatusPill({ completed, score }: { completed: boolean; score: number }) {
    if (!completed) {
      return (
        <span className="inline-flex items-center rounded-full bg-[hsl(var(--status-info-bg))] text-[hsl(var(--status-info))] border border-[hsl(var(--status-info)/0.3)] px-2 py-0.5 text-xs font-medium">
          Not Started
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))] border border-[hsl(var(--status-success)/0.3)] px-2 py-0.5 text-xs font-medium">
        <CheckCircle2 className="h-3 w-3" />
        Committed Steward
        <span className="text-muted-foreground font-normal">· {score}/100</span>
      </span>
    );
  }

  // Sync incoming auto-context into the persisted stewardship state.
  useEffect(() => {
    const changed = (Object.keys(autoContext) as (keyof StewardshipAuto)[])
      .some((k) => stewardship.auto[k] !== autoContext[k]);
    if (!changed) return;
    const score = computeStewardshipScore(stewardship.manual, autoContext);
    onStewardshipChange({
      manual: stewardship.manual,
      auto: autoContext,
      score,
      rating: stewardshipRating(score),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoContext]);

  const score = stewardship.score;
  const rating = stewardship.rating;
  const completed = stewardshipCompleted(stewardship);

  const toggleManual = (key: keyof StewardshipManual, checked: boolean) => {
    const manual = { ...stewardship.manual, [key]: checked };
    const newScore = computeStewardshipScore(manual, stewardship.auto);
    onStewardshipChange({
      manual,
      auto: stewardship.auto,
      score: newScore,
      rating: stewardshipRating(newScore),
    });
  };

  const updateFutureMe = (idx: number, value: string) => {
    const next = [...futureMe.answers];
    next[idx] = value;
    onFutureMeChange({ answers: next });
  };

  const sections: AssessmentItem["section"][] = ["A", "B", "C", "D", "E"];

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2 w-full">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            Family Banking Stewardship™ Assessment & Future Me Letter
            <span className="text-xs font-normal text-muted-foreground ml-1">(Optional)</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <StatusPill completed={completed} score={score} />
            <Button
              type="button"
              size="sm"
              variant={open ? "outline" : "default"}
              onClick={() => setOpen((o) => !o)}
            >
              {open ? (
                <><ChevronDown className="h-4 w-4 mr-1" /> Hide Assessment</>
              ) : (
                <><ChevronRight className="h-4 w-4 mr-1" /> {completed ? "Review Assessment" : "Start Assessment"}</>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-6">
          <p className="text-xs text-muted-foreground">
            Optional reflection. This assessment is informational only — it does not
            change calculations or block generating your Family Banking Commitment
            Agreement™. Check what is true today; come back later for the rest.
          </p>

          <div className="space-y-5">
            {sections.map((sec) => {
              const items = STEWARDSHIP_ITEMS.filter((i) => i.section === sec);
              const sectionTotal = items.reduce((sum, it) => sum + it.points, 0);
              const sectionEarned = items.reduce((sum, it) => {
                const checked =
                  it.kind === "auto"
                    ? stewardship.auto[it.key as keyof StewardshipAuto]
                    : stewardship.manual[it.key as keyof StewardshipManual];
                return sum + (checked ? it.points : 0);
              }, 0);
              return (
                <div key={sec}>
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="text-sm font-semibold">{SECTION_TITLES[sec]}</h3>
                    <span className="text-xs text-muted-foreground">
                      {sectionEarned}/{sectionTotal} pts
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {items.map((it) => {
                      const id = `steward-${it.key}`;
                      if (it.kind === "auto") {
                        const earned = stewardship.auto[it.key as keyof StewardshipAuto];
                        return (
                          <div key={id} className="flex items-start gap-3 rounded-md bg-muted/40 px-2 py-1.5">
                            <CheckCircle2
                              className={`h-4 w-4 mt-0.5 ${earned ? "text-primary" : "text-muted-foreground/40"}`}
                            />
                            <div className="flex-1 text-sm leading-snug">
                              {it.label}
                              <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                                Automatic · {it.points} pts
                              </span>
                            </div>
                          </div>
                        );
                      }
                      const checked = stewardship.manual[it.key as keyof StewardshipManual];
                      return (
                        <div key={id} className="flex items-start gap-3 px-2 py-1">
                          <Checkbox
                            id={id}
                            checked={checked}
                            onCheckedChange={(c) => toggleManual(it.key as keyof StewardshipManual, c === true)}
                            className="mt-0.5"
                          />
                          <Label htmlFor={id} className="cursor-pointer text-sm font-normal leading-snug flex-1">
                            {it.label}
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                              {it.points} pts
                            </span>
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-md border border-[hsl(var(--status-success)/0.3)] bg-[hsl(var(--status-success-bg))] p-3 text-sm">
            <span className="font-semibold">Current standing:</span>{" "}
            <span className="font-semibold text-[hsl(var(--status-success))]">
              {completed ? "Committed Steward" : "Not started"}
            </span>
            {completed && (
              <span className="ml-2 text-muted-foreground">— {rating} ({score}/100)</span>
            )}
          </div>

          {/* Future Me Letter */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Future Me Letter</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Leave any question blank and the printed report will show writing lines
              so you can fill it in by hand.
            </p>
            <div className="space-y-3">
              {FUTURE_ME_QUESTIONS.map((q, i) => (
                <div key={i}>
                  <Label className="text-sm font-medium">{q}</Label>
                  <Textarea
                    value={futureMe.answers[i] || ""}
                    onChange={(e) => updateFutureMe(i, e.target.value)}
                    placeholder="Your answer (optional)"
                    className="mt-1 text-sm min-h-[64px]"
                    maxLength={600}
                  />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
